import React from 'react'
import styled from 'styled-components'
import { Alert, Block, BlockGroup, Button, Tooltip, Text } from '@mote/ui'
import { useHistory } from 'react-router-dom'
import dayjs from 'dayjs'
import { CrownIcon, TickIcon } from '@mote/assets/icons'
import { AccountManageSubscription } from './/AccountManageSubscription'
import { getGodModeURLSuffix } from '../../shared/Utils'
import { Content } from '../Dashboard/components'

export const Account = ({ account }) => {
  const history = useHistory()
  return (
    <Root>
      {account.isInstitution && (
        <Block>
          <Content type='section' title='Organizational Plan' noPadding>
            <StripedBlock>
              <Row layout='horizontal' align='center'>
                <Property>Organization</Property>
                <Value>{account.institutionName}</Value>
              </Row>

              <Row layout='horizontal' align='center' zeroMinWidth>
                <Property>Owner</Property>
                <Tooltip content={account.accountOwnerEmail}>
                  <Value>
                    <TruncatedText>{account.accountOwnerEmail}</TruncatedText>
                  </Value>
                </Tooltip>
              </Row>

              {account.accountPureAdminEmails.length > 0 && (
                <Row layout='horizontal' align='center'>
                  <Property>Admins</Property>
                  <Block gap={8} style={{ overflow: 'hidden' }}>
                    {account.accountPureAdminEmails.map(admin => (
                      <Tooltip content={admin} key={admin}>
                        <Value>
                          <TruncatedText>{admin}</TruncatedText>
                        </Value>
                      </Tooltip>
                    ))}
                  </Block>
                </Row>
              )}

              {account.isInstitutionalAdmin && (
                <Row layout='horizontal' align='center'>
                  <Property>Plan Type</Property>
                  <Value>
                    {account.totalPaidSeats > 400
                      ? 'School'
                      : account.institutionProductSummary}
                  </Value>
                </Row>
              )}
              {account.isInstitutionalAdmin && (
                <Row layout='horizontal' align='center' justify='space-between'>
                  <Property>Users Assigned</Property>
                  <Value>
                    {account.totalUsedSeats} of {account.totalPaidSeats}
                  </Value>
                  <Button
                    text='Manage Users'
                    onClick={_ =>
                      history.push({
                        pathname: '/account/users',
                        search: getGodModeURLSuffix()
                      })
                    }
                    variant='link'
                  />
                </Row>
              )}
              {account.isInstitutionalAdmin && (
                <Row layout='horizontal' align='center'>
                  <Property>Expires</Property>
                  <Value>
                    {dayjs(account.subscriptionValidUntil).format(
                      'DD MMM YYYY'
                    )}
                  </Value>
                </Row>
              )}
            </StripedBlock>
          </Content>
        </Block>
      )}

      {account.isInstitution && <Divider />}

      {!account.isMinor && (
        <Content type='section' title='Personal Plan' noPadding>
          {account.freeTrialExpired &&
            !account.isOnReferralUnlimited &&
            !account.isVIP && (
              <Alert
                type='warn'
                message='Your free trial has expired'
                style={{ margin: 0 }}
              />
            )}
          <StripedBlock>
            <Row layout='horizontal' align='center'>
              <Property>Plan Type</Property>
              <Value>
                {account.isVIP ? (
                  <CrownIcon style={{ marginRight: 10 }} />
                ) : null}{' '}
                {account.productName}
              </Value>
            </Row>
            {!account.isVIP &&
              (!account.freeTrialExpired || account.isOnReferralUnlimited) &&
              (account.referralUnlimitedExpiryEpoch ??
                account.planExpiryEpoch ??
                account.freeTrialExpiryEpoch ??
                account.nextRenewalEpoch) && (
                <Row layout='horizontal' align='center'>
                  <Property>Expires</Property>
                  <Value>
                    {dayjs(
                      (account.referralUnlimitedExpiryEpoch ??
                        account.planExpiryEpoch ??
                        account.freeTrialExpiryEpoch ??
                        account.nextRenewalEpoch) * 1000
                    ).format('DD MMM YYYY')}
                  </Value>
                </Row>
              )}
          </StripedBlock>
        </Content>
      )}

      {account.isMinor && (
        <Content type='section' title='Under 18 plan' noPadding>
          <StripedBlock>
            <Row layout='horizontal' align='center'>
              <Property>Plan Type</Property>
              <Value>{account.productName}</Value>
            </Row>
            <Row layout='horizontal' align='center'>
              <Property>Motes recorded</Property>
              <Value>{account.description}</Value>
            </Row>
          </StripedBlock>
        </Content>
      )}

      {(account.isInFreeTrial ||
        (account.freeTrialExpired && !account.isOnReferralUnlimited)) &&
        !account.isVIP && (
          <>
            <Divider />
            <BlockGroup>
              <Block gap={24} align='flex-start'>
                <Text
                  fontSize={18}
                  color='#646F7B'
                  fontWeight={400}
                  lineHeight='120%'
                >
                  {account.freeTrialExpired
                    ? "You're missing out on all Mote features including:"
                    : 'When your trial expires you’ll be missing out on Mote features including:'}
                </Text>

                <BlockGroup gap={16}>
                  <Block layout='horizontal' gap={10}>
                    <TickIcon />
                    <Text
                      tag='p'
                      fontSize={15}
                      fontWeight={400}
                      lineHeight='120%'
                      color='#25252d'
                    >
                      Voice to text transcription
                    </Text>
                  </Block>
                  <Block layout='horizontal' align='center' gap={8}>
                    <TickIcon />
                    <Text
                      tag='p'
                      fontSize={15}
                      fontWeight={400}
                      lineHeight='120%'
                      color='#25252d'
                    >
                      Translate your mote transcripts
                    </Text>
                  </Block>
                  <Block layout='horizontal' align='center' gap={8}>
                    <TickIcon />
                    <Text
                      tag='p'
                      fontSize={15}
                      fontWeight={400}
                      lineHeight='120%'
                      color='#25252d'
                    >
                      Save and reuse motes with motebook
                    </Text>
                  </Block>
                  {account.isInstitution && (
                    <>
                      <Block layout='horizontal' align='center' gap={8}>
                        <TickIcon />
                        <Text
                          tag='p'
                          fontSize={15}
                          fontWeight={400}
                          lineHeight='120%'
                          color='#25252d'
                        >
                          Manage users & view usage
                        </Text>
                      </Block>
                      <Block layout='horizontal' align='center' gap={8}>
                        <TickIcon />
                        <Text
                          tag='p'
                          fontSize={15}
                          fontWeight={400}
                          lineHeight='120%'
                          color='#25252d'
                        >
                          Domain enablement
                        </Text>
                      </Block>
                      <Block layout='horizontal' align='center' gap={8}>
                        <TickIcon />
                        <Text
                          tag='p'
                          fontSize={15}
                          fontWeight={400}
                          lineHeight='120%'
                          color='#25252d'
                        >
                          Dedicated support, custom PD
                        </Text>
                      </Block>
                    </>
                  )}
                </BlockGroup>

                <Button
                  text='Upgrade Now!'
                  onClick={_ => history.push('/pricing')}
                />
              </Block>

              <ReferAFriend gap={25} align='flex-start' margin='40px 0 0 0'>
                <Block gap={16}>
                  <Text
                    tag='p'
                    fontSize={18}
                    fontWeight={500}
                    lineHeight='120%'
                  >
                    📣{' '}
                    {account.isInFreeTrial
                      ? 'Extend your trial - Refer a friend!'
                      : `Want to reactivate ${account.productName} for free?`}
                  </Text>
                  <Text
                    tag='p'
                    color='#646F7B'
                    fontSize={15}
                    fontWeight={400}
                    lineHeight='140%'
                  >
                    {account.isInFreeTrial
                      ? `Every new user who joins using your invite code will extend your
              free Unlimited trial by 30 days`
                      : 'For every new user who joins using your invite code, we will add another 30 days of Unlimited access.'}
                  </Text>
                </Block>
                <Button
                  text='Get my code'
                  onClick={_ => history.push('/account/referrals')}
                />
              </ReferAFriend>
            </BlockGroup>
          </>
        )}

      {((account.isPaid && !account.isInstitution) ||
        (account.isPaid &&
          account.isInstitution &&
          account.isInstitutionalAdmin)) &&
        account.canShowButton && (
          <>
            <Divider />
            <Content
              type='section'
              title='Manage subscription'
              subtitle='Manage your subscription settings or cancel your plan'
              noPadding
            >
              <AccountManageSubscription />
            </Content>
          </>
        )}
    </Root>
  )
}

const Root = styled.div`
  max-width: 540px;
  width: 100%;
`

const StripedBlock = styled(Block)`
  & > :nth-of-type(odd) {
    background: #f9f9fb;
  }
`

const Row = styled(Block)`
  padding: 22px 16px;
  border-radius: 4px;
`

const Property = styled.span`
  color: #232323;
  font-size: 15px;
  line-height: 120%;
  font-weight: 500;
  text-transform: capitalize;
  flex: 0 0 240px;

  @media (max-width: 639px) {
    flex: 0 0 130px;
  }
`

const Value = styled.span`
  color: #646f7b;
  font-size: 15px;
  line-height: 120%;
  font-weight: 400;
  text-align: left;
  width: 100%;
  display: flex;
  align-items: center;
  min-width: 0;
`

const TruncatedText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
`

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background: #ececf3;
  margin: 40px 0;
`

const ReferAFriend = styled(Block)`
  background: #fbf3fd;
  border-radius: 8px;
  padding: 24px;
`
